import React, { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    Dos: any;
  }
}

const DOSEmulator: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dosInstanceRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [availableGames, setAvailableGames] = useState<string[]>([]);
  const [selectedGame, setSelectedGame] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Load js-dos library
    loadJsDosLibrary();
    fetchAvailableGames();
  }, []);

  const loadJsDosLibrary = () => {
    // Check if js-dos is already loaded
    if (window.Dos) {
      setIsLoaded(true);
      return;
    }

    // Load js-dos from CDN
    const script = document.createElement('script');
    script.src = 'https://js-dos.com/6.22/current/js-dos.js';
    script.onload = () => {
      setIsLoaded(true);
    };
    script.onerror = () => {
      setError('Failed to load js-dos library');
    };
    document.head.appendChild(script);
  };

  const fetchAvailableGames = async () => {
    try {
      const response = await fetch('/api/roms/dos');
      const data = await response.json();
      setAvailableGames(data.files || []);
    } catch (error) {
      console.error('Failed to fetch DOS games:', error);
      setAvailableGames([]);
    }
  };

  const loadGame = async (gameName: string) => {
    if (!window.Dos || !canvasRef.current) {
      setError('js-dos not loaded or canvas not available');
      return;
    }

    try {
      setError('');
      setIsLoading(true);
      
      // Stop any existing DOS instance
      if (dosInstanceRef.current) {
        dosInstanceRef.current.exit();
        dosInstanceRef.current = null;
      }

      const response = await fetch(`/roms/dos/${gameName}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load game: ${response.statusText}`);
      }

      const gameData = await response.arrayBuffer();
      
      // Initialize js-dos
      const dosInstance = window.Dos(canvasRef.current, {
        wdosboxUrl: 'https://js-dos.com/6.22/current/wdosbox.js',
      });

      // Load and run the game
      dosInstance.ready((fs: any, main: any) => {
        fs.extract(gameData).then(() => {
          main(['-c', 'dir', '-c', 'exit']).then(() => {
            setSelectedGame(gameName);
            setIsPlaying(true);
            setIsLoading(false);
            dosInstanceRef.current = dosInstance;
          });
        });
      });

    } catch (error) {
      setError(`Error loading game: ${error}`);
      setIsLoading(false);
      console.error('Game loading error:', error);
    }
  };

  const stopGame = () => {
    if (dosInstanceRef.current) {
      dosInstanceRef.current.exit();
      dosInstanceRef.current = null;
      setIsPlaying(false);
      setSelectedGame('');
    }
  };

  const toggleFullscreen = () => {
    if (canvasRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        canvasRef.current.requestFullscreen();
      }
    }
  };

  return (
    <div className="dos-emulator">
      <div className="dos-controls" style={{ marginBottom: '10px' }}>
        <select 
          value={selectedGame} 
          onChange={(e) => setSelectedGame(e.target.value)}
          disabled={isPlaying || isLoading}
        >
          <option value="">Select a DOS game...</option>
          {availableGames.map(game => (
            <option key={game} value={game}>{game}</option>
          ))}
        </select>
        
        <button 
          onClick={() => selectedGame && loadGame(selectedGame)}
          disabled={!selectedGame || isPlaying || isLoading}
          style={{ marginLeft: '10px' }}
        >
          {isLoading ? 'Loading...' : 'Load Game'}
        </button>
        
        {isPlaying && (
          <>
            <button onClick={stopGame} style={{ marginLeft: '10px' }}>
              Stop
            </button>
            <button onClick={toggleFullscreen} style={{ marginLeft: '10px' }}>
              Fullscreen
            </button>
          </>
        )}
      </div>

      {error && (
        <div style={{ color: 'red', marginBottom: '10px' }}>
          {error}
        </div>
      )}

      <div className="dos-screen">
        <canvas
          ref={canvasRef}
          style={{
            border: '2px solid #000',
            width: '640px',
            height: '400px',
            backgroundColor: '#000'
          }}
        />
      </div>

      <div className="dos-instructions" style={{ marginTop: '10px', fontSize: '12px' }}>
        <p><strong>DOS Emulator Instructions:</strong></p>
        <p>Use keyboard and mouse to control DOS games. Different games have different controls.</p>
        <p>Common DOS commands: DIR (list files), CD (change directory), [GAME].EXE (run game)</p>
        {!selectedGame && (
          <p style={{ color: '#666' }}>
            Upload DOS game ZIP files to the <code>/public/roms/dos/</code> folder to play games.
            Make sure the ZIP contains the game files and an executable.
          </p>
        )}
      </div>
    </div>
  );
};

export default DOSEmulator; 